import { useRequestPhoneVerificationTokenMutation } from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { Button } from 'src/components/button/button'
import InputField from 'src/components/forms/input_field/input_field'
import { SvgIcon } from 'src/components/icons/svg-icon'
import { ProgressIndicator } from 'src/components/progress_indicator/progress_indicator'
import { CLASS_PREFIX } from 'src/constants'
import { textResources } from 'src/lang/de'
import { REGEXP_INVALID_PHONE } from 'src/utils/pattern/pattern'
import { AUTH_CLS } from '../../auth/constants'
import './phone_verification.scss'

type IProps = {
  onNext: (phoneNumber: string) => void
}

const cls = `${CLASS_PREFIX}phone-verification`

export const PhoneVerification: React.FC<IProps> = ({ onNext }) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [requestPhoneVerificationToken] = useRequestPhoneVerificationTokenMutation()

  const handlePhoneChange = useCallback((value: string) => {
    if (REGEXP_INVALID_PHONE.test(value)) return
    setPhoneNumber(value)
    setErrorMessage('')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!phoneNumber.trim()) {
      setErrorMessage(textResources.onboardingSettings.invalidPhoneNumber)
      return
    }

    setIsLoading(true)
    setErrorMessage('')

    try {
      const res = await requestPhoneVerificationToken({
        variables: { input: { newPhone: phoneNumber } }
      })

      if (res.data?.requestPhoneVerificationToken?.errors?.length) {
        throw res.data.requestPhoneVerificationToken.errors[0]
      }

      onNext(phoneNumber)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.code === 'max_verification_attempts_exceeded') {
        setErrorMessage(textResources.shared.errorUnknown)
      } else if (error?.type === 'taken') {
        setErrorMessage(textResources.onboardingSettings.alreadyExistPhoneNumber)
      } else {
        setErrorMessage(textResources.onboardingSettings.invalidPhoneNumber)
      }
    } finally {
      setIsLoading(false)
    }
  }, [phoneNumber, requestPhoneVerificationToken, onNext])

  return (
    <div className={cls}>
      <div className={`${cls}__header`}>
        <div />
        <ProgressIndicator steps={3} current={2} />
      </div>
      <div className={`${cls}__content`}>
        <div>
          <div className={`${AUTH_CLS}__header`}>{textResources.authentication.phoneVerificationTitle}</div>
          <div className={`${AUTH_CLS}__subheader`}>
            {textResources.authentication.phoneVerificationSubtitle}
          </div>
        </div>
        <div>
          <InputField
            floatLabel={false}
            label={textResources.authentication.phoneNumber}
            name="phone"
            placeholder={textResources.authentication.enterNumber}
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            valid={!errorMessage}
            errorText={errorMessage || undefined}
          />
          <div className={`${cls}__privateHint`}>
            <SvgIcon name='Security/lock-01' size={16} /> {textResources.authentication.yourPhoneIsPrivate}
          </div>
        </div>
        <Button
          size="large"
          title={textResources.authentication.next}
          disabled={isLoading || !phoneNumber.trim()}
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}
